import { useCallback, useEffect, useState } from "react";
import { fetchAdsArchiveList, fetchAdsArchiveSignedUrl } from "@/lib/adsAssistantClientEngagement";

type ArchiveFile = { name: string; updated_at: string | null; created_at: string | null };

export function AdsAssistantOrgSettingsArchiveList(props: { enabled: boolean; ready: boolean }) {
  const { enabled, ready } = props;
  const [files, setFiles] = useState<ArchiveFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled || !ready) {
      setFiles([]);
      setError(null);
      setNotice(null);
      return;
    }
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const r = await fetchAdsArchiveList();
      if (!r.ok) {
        setFiles([]);
        setError(r.error);
        return;
      }
      const d = r.data as {
        ok?: boolean;
        archive_enabled?: boolean;
        files?: ArchiveFile[];
        message?: string;
      };
      if (d && typeof d === "object" && d.archive_enabled === false && typeof d.message === "string") {
        setNotice(d.message);
        setFiles([]);
        return;
      }
      setFiles(Array.isArray(d?.files) ? d.files : []);
    } finally {
      setLoading(false);
    }
  }, [enabled, ready]);

  const downloadArchive = useCallback(async (name: string) => {
    setSigning(name);
    setError(null);
    try {
      const r = await fetchAdsArchiveSignedUrl(name);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      const d = r.data as { url?: string };
      if (d?.url) window.open(d.url, "_blank", "noopener,noreferrer");
    } finally {
      setSigning(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!enabled || !ready) return null;

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2.5 dark:border-gray-600 dark:bg-zinc-800/40">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">Recent archives</span>
        <button
          type="button"
          className="text-xs text-blue-600 hover:underline dark:text-blue-400 disabled:opacity-50"
          disabled={loading}
          onClick={() => void load()}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
        Download links are signed and expire in about 10 minutes.
      </p>
      {error ? (
        <p className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p>
      ) : notice ? (
        <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">{notice}</p>
      ) : files.length === 0 && !loading ? (
        <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
          No .jsonl files yet under this org&apos;s archive path (run retention after there are expired posts).
        </p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {files.map((f) => (
            <li
              key={f.name}
              className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-800 dark:text-gray-200"
            >
              <span className="font-mono break-all">{f.name}</span>
              <button
                type="button"
                className="shrink-0 text-blue-600 hover:underline dark:text-blue-400 disabled:opacity-50"
                disabled={!!signing}
                onClick={() => void downloadArchive(f.name)}
              >
                {signing === f.name ? "…" : "Download"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
